// ActionButtons.jsx
import React, { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faSpinner,
  faEye,
  faStethoscope as faConsultIcon,
  faUserInjured as faInpatientIcon,
  faBandage as faOutpatientIcon,
  faVial as faDiagnosticIcon,
  faPlay,
  faBan,
  faXmark,
  faExclamationTriangle
} from "@fortawesome/free-solid-svg-icons";

const getButtonText = (flowType) => {
  switch(flowType) {
    case "TYPE_1":
      return "Continue Generate LOA";
    case "TYPE_2":
      return "Continue Generate LOA";
    case "TYPE_3":
      return "Perform VOC (Verification Of Coverability)";
    case "TYPE_4":
      return "Process OPD-OR";
    default:
      return "Continue";
  }
};

/**
 * Reusable Action Buttons for modals
 *
 * @param {Function} onViewMemberInfo - Triggered when "View Member Info" is clicked
 * @param {Function} onContinue - Triggered when "Continue" is clicked
 * @param {Function} onCancel - Triggered when "Cancel This TRN" is clicked
 * @param {Function} onClose - Triggered when "Close" is clicked
 * @param {string} closeConfirmMessage - Custom confirmation message for close action
 */
const ActionButtons = ({ 
  onViewMemberInfo, 
  onContinue, 
  onCancel, 
  onClose, 
  closeConfirmMessage = "Are you sure you want to close or leave this transaction? Any unsaved changes will be lost." ,
  flowType,
  continueDisabled = false,
  cancelDisabled = false,
  disabledReason = "This transaction can no longer be edited.",
}) => {
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const handleCloseClick = () => {
    setShowCloseConfirm(true);
  };

  const handleConfirmClose = () => {
    setShowCloseConfirm(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowCloseConfirm(false);
  };

  const handleContinueClick = () => {
    if (continueDisabled) return;
    onContinue();
  };

  const handleCancelClick = () => {
    if (cancelDisabled) return;
    onCancel();
  };

  return (
    <>
      <div className="flex flex-wrap gap-3 mt-6 pt-4 border-t border-gray-200 justify-end">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors"
          onClick={onViewMemberInfo}
        >
          <FontAwesomeIcon icon={faEye} />
          View Member Info
        </motion.button>

        <motion.button
          whileHover={continueDisabled ? undefined : { scale: 1.02 }}
          whileTap={continueDisabled ? undefined : { scale: 0.98 }}
          disabled={continueDisabled}
          title={continueDisabled ? disabledReason : undefined}
          className={`px-4 py-2 text-white font-medium rounded-lg flex items-center gap-2 transition-colors ${
            continueDisabled
              ? "cursor-not-allowed bg-gray-400 opacity-70"
              : "bg-green-600 hover:bg-green-700"
          }`}
          onClick={handleContinueClick}
        >
          <FontAwesomeIcon icon={faPlay} />
          {getButtonText(flowType)}
        </motion.button>

        <motion.button
          whileHover={cancelDisabled ? undefined : { scale: 1.02 }}
          whileTap={cancelDisabled ? undefined : { scale: 0.98 }}
          disabled={cancelDisabled}
          title={cancelDisabled ? disabledReason : undefined}
          className={`px-4 py-2 text-white font-medium rounded-lg flex items-center gap-2 transition-colors ${
            cancelDisabled
              ? "cursor-not-allowed bg-gray-400 opacity-70"
              : "bg-yellow-600 hover:bg-yellow-700"
          }`}
          onClick={handleCancelClick}
        >
          <FontAwesomeIcon icon={faBan} />
          Cancel This TRN
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 flex items-center gap-2 transition-colors"
          onClick={handleCloseClick}
        >
          <FontAwesomeIcon icon={faXmark} />
          Close
        </motion.button>
      </div>

      {/* Close Confirmation Modal */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {showCloseConfirm && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-y-0 left-0 z-[10000] tracker-aware-modal-layer flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
              >
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  className="w-full max-w-md rounded-2xl border border-gray-100 bg-white p-6 shadow-2xl"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 text-lg" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">Confirm Close</h3>
                  </div>

                  <p className="text-gray-700 mb-6">
                    {closeConfirmMessage}
                  </p>

                  <div className="flex justify-end gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-5 py-2.5 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors"
                      onClick={handleCancelClose}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-5 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                      onClick={handleConfirmClose}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                      Yes, Close
                    </motion.button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </>
  );
};

export default ActionButtons;
